import React from 'react';
import PropTypes from 'prop-types';
import useAppContext from '../../../context/useAppContext';
import ZoneModel from '../../../models/zoneModel';
import Category from '../../../models/category';
import Contingent from '../../../models/contingentModel';
import TicketOriginFilter from '../../../models/ticketOriginFilter';
import { useHistory } from 'react-router-dom';
import PartnerModel from '../../../models/partner';
import EventVariantModel from '../../../models/eventVariantModel';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

import moment from 'moment-timezone';
import RatesTreeModel from '../../../models/ratesTreeModel';
import { fetcher } from '@weezevent/weezjs-core';

export const ManagerContext = React.createContext(null);

export function ManagerContextProvider({ children }) {
    const [context, setContext] = React.useState({});

    const removeFromContext = React.useCallback(names => {
        // Ensure that multiple keys can be removed at once
        if (!Array.isArray(names)) {
            names = [names];
        }
        setContext(context => {
            let newContext = { ...context };
            names.forEach(name => {
                delete newContext[name];
            });
            return newContext;
        });
    }, []);

    return (
        <ManagerContext.Provider
            value={{
                context,
                setContext,
                removeFromContext
            }}
        >
            {children}
        </ManagerContext.Provider>
    );
}

ManagerContextProvider.propTypes = {
    children: PropTypes.node
};

export function handleSearchQuery(spec, filters, truncate = false) {
    let query = {};
    Object.entries(filters).forEach(([key, value]) => {
        if (truncate && !spec.fields?.includes(key)) {
            // Ignore key that are not in the spec fields in case we don't need all the object
            // For example when we want to call API we don't want all the filters,
            // just those who are useful in this case
            return;
        }

        if (value instanceof Set) {
            query[key] = Array.from(value).join(',');
            return;
        }

        // case Filter date
        if (spec.date?.includes(key) && value instanceof Object && !Array.isArray(value)) {
            Object.entries(value).forEach(([_key, _value]) => {
                if (_value) {
                    query[`${key}_${_key}`] = moment(_value)
                        .set({ second: 0, millisecond: 0 })
                        .toISOString();
                }
            });
            return;
        }
        // case Filter form
        if (spec.form?.includes(key) && value instanceof Object && !Array.isArray(value)) {
            Object.entries(value).forEach(([_key, _value]) => {
                query[`${_key}`] = _value;
            });
            return;
        }

        query[key] = value;
    });

    return query;
}

export const extractParameters = (spec, queryString) => {
    // Strip the `?` from the querystring
    const query = queryString.substr(1);

    const parameters = {};

    // handle empty strings
    if (!query) {
        return parameters;
    }

    for (let [key, value] of new URLSearchParams(query).entries()) {
        key = key in spec.aliases ? spec.aliases[key] : key;
        if (spec.multiple.includes(key)) {
            parameters[key] = new Set(value.split(','));
            continue;
        }

        let isAlreadySetted = false;
        spec.date?.forEach(filterName => {
            //key must begin by filter name
            if (key.indexOf(filterName) === 0) {
                if (!parameters.hasOwnProperty.call(parameters, filterName)) {
                    parameters[filterName] = {
                        after: null,
                        before: null
                    };
                }
                parameters[filterName][key.replace(`${filterName}_`, '')] = value;
                isAlreadySetted = true;
            }
        });

        //forms filter
        spec.form?.forEach(filterName => {
            //key must begin by filter name
            if (key.indexOf(filterName) === 0) {
                if (!parameters.hasOwnProperty.call(parameters, filterName)) {
                    parameters[filterName] = {};
                }
                parameters[filterName][key] = value;
                isAlreadySetted = true;
            }
        });

        if (!isAlreadySetted) {
            parameters[key] = value;
        }
    }
    return parameters;
};

export function useFilters(spec, initialFilters = {}) {
    const history = useHistory();

    const { context = {}, setContext } = React.useContext(ManagerContext);
    const filtersFromUrl = React.useMemo(() => extractParameters(spec, history.location.search), [spec, history.location.search]);
    const filters = (Object.keys(filtersFromUrl).length > 0 && filtersFromUrl) || context.filters || initialFilters;

    // If aliases is set, we wan't to check if some keys in filters context corresponded to some
    // filters in new spec. If we find an alias for the key found in the filters. We delete this key
    // and replace it with his alias
    if ('aliases' in spec) {
        Object.keys(filters).forEach(key => {
            if (key in spec.aliases) {
                filters[spec.aliases[key]] = filters[key];
                delete filters[key];
            }
        });
    }

    const setFilters = React.useCallback(
        (filters = {}) => {
            setContext(ctx => {
                let search = handleSearchQuery(spec, filters);
                if (ctx.size) {
                    search.size = ctx.size;
                }
                if (ctx.page) {
                    search.page = ctx.page + 1;
                }
                if (ctx.sort) {
                    search.sort = ctx.sort;
                }

                history.push({
                    search: Object.entries(search)
                        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                        .join('&')
                });
                return {
                    ...ctx,
                    filters: filters
                };
            });
        },
        [history]
    );
    return [filters, setFilters];
}

export function usePageSize(defaultPageSize = 50) {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const queryParams = new URLSearchParams(history.location.search);
    const { size = queryParams.has('size') ? Number(queryParams.get('size')) : defaultPageSize } = context;

    const setPageSize = React.useCallback(
        _size => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);
                queryParams.set('size', _size);
                history.push({
                    search: queryParams.toString()
                });
                return { ...ctx, size: _size };
            });
        },
        [history]
    );

    //for add pageSize in url for first render
    React.useEffect(() => {
        setPageSize(size);
    }, []);

    return [size, setPageSize];
}

export function useCurrentPage(defaultCurrentPage = 0) {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const queryParams = new URLSearchParams(history.location.search);
    const { page = queryParams.has('page') ? Number(queryParams.get('page')) - 1 : defaultCurrentPage } = context;

    const setCurrentPage = React.useCallback(
        _page => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);

                //we place the page in the first position for better visibility for the user
                queryParams.delete('page');
                history.push({
                    search: `page=${_page + 1}${queryParams.toString() ? `&${queryParams.toString()}` : ''}`
                });
                return { ...ctx, page: _page };
            });
        },
        [history]
    );

    //for add page in url for first render
    React.useEffect(() => {
        setCurrentPage(page);
    }, []);

    return [page, setCurrentPage];
}

export function useSmartTableQuery(key) {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const queryParams = new URLSearchParams(history.location.search);
    const value = context[key] || queryParams.get(key);

    console.log(history.location.search);

    const setValue = React.useCallback(
        newValue => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);

                queryParams.delete(key);
                queryParams.set(key, newValue);

                history.push({
                    search: queryParams.toString()
                });

                return { ...ctx, [key]: newValue };
            });
        },
        [history]
    );

    React.useEffect(() => {
        setValue(value);
    }, []);

    return [value, setValue];
}

export function useSort(defaultSort = '') {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const queryParams = new URLSearchParams(history.location.search);
    const { sort = queryParams.has('sort') ? queryParams.get('sort') : defaultSort } = context;

    const setSort = React.useCallback(
        _sort => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);
                queryParams.set('sort', _sort);
                history.push({
                    search: queryParams.toString()
                });
                return { ...ctx, sort: _sort };
            });
        },
        [history]
    );

    //for add sort in url for first render
    React.useEffect(() => {
        if (sort) {
            setSort(sort);
        }
    }, []);

    return [sort, setSort];
}

export function useRates() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { rates = [], loadingRates = true, syncRates = () => {} } = context;

    const [ratesApi, loading, sync] = RatesTreeModel.useApiModel( // eslint-disable-line
        {
            eventId: event.pk,
            organizationId: organization.pk,
            onSuccess: response => {
                setContext(ctx => {
                    return { ...ctx, rates: response, loadingRates: false, syncRates: sync };
                });
            },
            onError: response => {
                setContext(ctx => {
                    return { ...ctx, rates: [], loadingRates: false, syncRates: sync };
                });
            }
        },
        [event, organization]
    );

    return [rates, loadingRates, syncRates];
}

export function useZones() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { zones = [], loadingZones = true, syncZones = () => {} } = context;

    if (event.is_seated) {
        const [zonesApi, loading, sync] = ZoneModel.useApiModel( // eslint-disable-line
            {
                organizationId: organization.pk,
                chartId: event.chart,
                onSuccess: response => {
                    setContext(ctx => {
                        return { ...ctx, zones: response, loadingZones: false, syncZones: sync };
                    });
                },
                onError: response => {
                    setContext(ctx => {
                        return { ...ctx, zones: [], loadingZones: false, syncZones: sync };
                    });
                }
            },
            [event, organization]
        );
    }

    return [zones, loadingZones, syncZones];
}

export function useCategories() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { categories = [], loadingCategories = true, syncCategories = () => {} } = context;

    if (event.is_seated) {
        const [categoriesApi, loading, sync] = Category.useApiModel(  // eslint-disable-line
            {
                organizationId: organization.pk,
                chartId: event.chart,
                onSuccess: response => {
                    setContext(ctx => {
                        return { ...ctx, categories: response, loadingCategories: false, syncCategories: sync };
                    });
                },
                onError: response => {
                    setContext(ctx => {
                        return { ...ctx, categories: [], loadingCategories: false, syncCategories: sync };
                    });
                }
            },
            [event, organization]
        );
    }

    return [categories, loadingCategories, syncCategories];
}

export function useContingents() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { contingents = [], loadingContingents = true, syncContingents = () => {} } = context;

    if (event.is_seated) {
        const [contingentsApi, loading, sync] = Contingent.useApiModel( // eslint-disable-line
            {
                organizationId: organization.pk,
                chartId: event.chart,
                onSuccess: response => {
                    setContext(ctx => {
                        return { ...ctx, contingents: response, loadingContingents: false, syncContingents: sync };
                    });
                },
                onError: response => {
                    setContext(ctx => {
                        return { ...ctx, contingents: [], loadingContingents: false, syncContingents: sync };
                    });
                }
            },
            [event, organization]
        );
    }

    return [contingents, loadingContingents, syncContingents];
}

export function usePartners() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { partners = [], loadingPartners = true, syncPartners = () => {} } = context;

    const [partnersApi, loading, sync] = PartnerModel.useApiModel( // eslint-disable-line
        {
            organizationId: organization.pk,
            onSuccess: response => {
                setContext(ctx => {
                    return { ...ctx, partners: response, loadingPartners: false, syncPartners: sync };
                });
            },
            onError: response => {
                setContext(ctx => {
                    return { ...ctx, partners: [], loadingPartners: false, syncPartners: sync };
                });
            }
        },
        [event, organization]
    );

    return [partners, loadingPartners, syncPartners];
}

export function useInventories(model) {
    // We only load the first 100 items initially
    const [limit, setLimit] = React.useState(100);
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    const { inventories = [], loadingInventories = true, syncInventories = () => {} } = context;

    let params = React.useMemo(() => {
        let _params = {
            eventId: event.pk,
            organizationId: organization.pk
        };

        if (event.use_sessions) {
            _params['query'] = {
                limit
            };
            return _params;
        }

        return { ..._params, chartId: event.chart, launch: Boolean(event.is_seated) };
    }, [event, organization, limit]);

    const [inventoriesApi, loading, sync] = model.useApiModel( // eslint-disable-line
        {
            ...params,
            onSuccess: response => {
                setContext(ctx => {
                    return { ...ctx, inventories: response, loadingInventories: false, syncInventories: sync };
                });
            },
            onError: response => {
                setContext(ctx => {
                    return { ...ctx, inventories: [], loadingInventories: false, syncInventories: sync };
                });
            }
        },
        [event, organization, params]
    );

    const requestFullListLoad = React.useCallback(() => {
        // The inventories can get huge. We load the full list on demand only
        if (inventories.length === limit) {
            setLimit(10000);
        }
    }, [limit, inventories.length]);

    return [inventories, loadingInventories || loading, syncInventories, requestFullListLoad];
}

const formatOptionsFilters = response => {
    let optionsOriginDetails = [];
    let optionsDiscount = [];
    Object.entries(response).forEach(([key, value]) => {
        if (['__meta', '__params'].includes(key) || !Array.isArray(value) || !value.length) {
            return;
        }
        if (key === 'discount') {
            optionsDiscount = value.map(filter => ({ key: filter, name: filter, text: filter }));
            return;
        }
        optionsOriginDetails = optionsOriginDetails.concat(value.map(filter => ({ key: filter, name: filter, text: filter })));
    });

    return { optionsOriginDetails: optionsOriginDetails, optionsDiscount: optionsDiscount };
};

export function useOriginDetails() {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    let { originDetails = [], loadingOriginDetails = true, originDetailsSync = () => {}, optionsOriginDetails = [], optionsDiscount = [] } = context;

    const [originDetailsAPi, loading, sync] = TicketOriginFilter.useApiModel( // eslint-disable-line
        {
            eventId: event.pk,
            organizationId: organization.pk,
            onSuccess: response => {
                let options = formatOptionsFilters(response);
                setContext(ctx => {
                    return { ...ctx, originDetails: response, loadingOriginDetails: false, originDetailsSync: sync, ...options };
                });
            },
            onError: response => {
                setContext(ctx => {
                    return { ...ctx, originDetails: [], loadingOriginDetails: false, originDetailsSync: sync };
                });
            }
        },
        [event, organization, formatOptionsFilters]
    );

    return [originDetails, loadingOriginDetails, originDetailsSync, optionsOriginDetails, optionsDiscount];
}

export function useCrossSelling(buyer = null) {
    const [{ event, organization }] = useAppContext();
    const { context = {}, setContext } = React.useContext(ManagerContext);
    let { crossSelling = [], loadingCrossSelling = true, crossSellingSync = () => {}, optionsCrossSeling = [] } = context;

    const queryParam = {};
    // if buyer is null, retrieve product for both attendee and buyer
    if (buyer !== null) {
        queryParam['for_buyer'] = buyer;
    }
    const [crossSellingAPi, loading, sync] = EventVariantModel.useApiModel( // eslint-disable-line
        {
            eventId: event.pk,
            organizationId: organization.pk,
            query: queryParam,
            onSuccess: response => {
                setContext(ctx => {
                    return { ...ctx, crossSelling: response, loadingCrossSelling: false, crossSellingSync: sync };
                });
            },
            onError: response => {
                setContext(ctx => {
                    return { ...ctx, crossSelling: [], loadingCrossSelling: false, crossSellingSync: sync };
                });
            }
        },
        [event, organization]
    );

    return [crossSelling, loadingCrossSelling, crossSellingSync, optionsCrossSeling];
}

export function useManagerContext() {
    const ctx = React.useContext(ManagerContext);
    if (!ctx) {
        throw new Error('[useSeatedContext] Did you use useSeatedContext outside a SeatedContextProvider?');
    }
    const { context, setContext, removeFromContext } = ctx;

    return [context, setContext, removeFromContext];
}

export function useFormFields() {
    const [{ event, organization }] = useAppContext();
    const [formFields, setFormFields] = React.useState(null);
    const [loadingFormField, setLoadingFormField] = React.useState(null);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    React.useEffect(() => {
        setLoadingFormField(true);
        fetcher
            .get(`/ticket/organizations/${organization.pk}/events/${event.pk}/form_fields`, { cache: false })
            .then(({ response }) => {
                setFormFields(response);
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.manager.filters.form.toast-error'));
            })
            .finally(() => setLoadingFormField(false));
    }, [event, organization]);

    return [formFields, loadingFormField];
}
